import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Lock, Mail, KeyRound, AlertCircle, CheckCircle, Loader2, AlertTriangle } from 'lucide-react';

type Step = 'email' | 'otp' | 'live-confirm';
type TradingMode = 'PAPER' | 'LIVE';

export function Login() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isAuthenticated, checkAuth, loading: authLoading } = useAuth();

  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [tradingMode, setTradingMode] = useState<TradingMode>('PAPER');
  const [liveConfirmText, setLiveConfirmText] = useState('');
  const [liveConfirmChecked, setLiveConfirmChecked] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [invitationToken, setInvitationToken] = useState<string | null>(null);

  useEffect(() => {
    // Check if coming from invitation link
    const token = searchParams.get('token');
    if (token) {
      validateInvitation(token);
    }
  }, [searchParams]);

  // Redirect to home (or `next`) if already authenticated
  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      const nextParam = searchParams.get('next');
      if (nextParam && nextParam.startsWith('/')) {
        navigate(nextParam, { replace: true });
      } else {
        navigate('/', { replace: true });
      }
    }
  }, [authLoading, isAuthenticated, navigate, searchParams]);

  const validateInvitation = async (token: string) => {
    try {
      const result = await api.validateInvitation(token);
      if (result.valid && result.email) {
        setInvitationToken(token);
        setEmail(result.email);
        setMessage('You have been invited! Enter the OTP sent to your email to continue.');
      } else {
        setError(result.message || 'Invalid invitation link');
      }
    } catch (err) {
      setError('Invalid invitation link');
    }
  };

  const handleRequestOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setMessage('');
    setLoading(true);

    try {
      const response = await api.requestOTP(email);
      setMessage(response.message);
      setStep('otp');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setMessage('');
    setLoading(true);

    try {
      // If LIVE mode selected, go to confirmation step first
      if (tradingMode === 'LIVE') {
        setStep('live-confirm');
        setLoading(false);
        return;
      }
      
      // PAPER mode - proceed directly
      await api.verifyOTP(email, otp, tradingMode, false);
      setMessage('Login successful!');
      // Refresh auth state in context before navigating
      await checkAuth();
      // Navigation will happen automatically via useEffect when isAuthenticated becomes true
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Invalid OTP code');
    } finally {
      setLoading(false);
    }
  };

  const handleLiveConfirm = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setMessage('');
    
    // Validate confirmation
    if (!liveConfirmChecked) {
      setError('You must acknowledge that you understand the risks');
      return;
    }
    
    if (liveConfirmText.toUpperCase() !== 'LIVE') {
      setError('Please type "LIVE" exactly to confirm');
      return;
    }
    
    setLoading(true);
    try {
      await api.verifyOTP(email, otp, tradingMode, true);
      setMessage('Login successful with LIVE trading mode!');
      // Refresh auth state in context before navigating
      await checkAuth();
      // Navigation will happen automatically via useEffect when isAuthenticated becomes true
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to activate LIVE trading');
    } finally {
      setLoading(false);
    }
  };

  const handleBackToEmail = () => {
    setStep('email');
    setOtp('');
    setError('');
    setMessage('');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-green-100 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-lg shadow-xl border border-gray-200 overflow-hidden">
          <div className="bg-emerald-700 p-6 text-center">
            <div className="w-16 h-16 rounded-full bg-white flex items-center justify-center mx-auto mb-4 shadow-md">
              <Lock className="w-8 h-8 text-emerald-700" />
            </div>
            <h1 className="text-2xl font-bold text-white">Welcome</h1>
            <p className="text-emerald-100 mt-2">PortfoliYo Trade</p>
          </div>

          <div className="p-8">
            {error && (
              <div className="mb-6 flex items-start gap-3 px-4 py-3 rounded-lg bg-red-50 border border-red-200 text-red-900">
                <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                <span className="text-sm">{error}</span>
              </div>
            )}

            {message && (
              <div className="mb-6 flex items-start gap-3 px-4 py-3 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-900">
                <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                <span className="text-sm">{message}</span>
              </div>
            )}

            {step === 'email' ? (
              <form onSubmit={handleRequestOTP} className="space-y-6">
                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                    Email Address
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      disabled={loading || !!invitationToken}
                      required
                      className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                      placeholder="your@email.com"
                    />
                  </div>
                  {invitationToken && (
                    <p className="mt-2 text-sm text-emerald-600 font-medium">
                      This email has been pre-filled from your invitation
                    </p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={loading || !email}
                  className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-emerald-700 text-white font-semibold rounded-lg hover:bg-emerald-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-md"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Mail className="w-5 h-5" />
                      Send Login Code
                    </>
                  )}
                </button>
              </form>
            ) : step === 'otp' ? (
              <form onSubmit={handleVerifyOTP} className="space-y-6">
                <div>
                  <label htmlFor="otp" className="block text-sm font-semibold text-gray-700 mb-2">
                    Enter 6-Digit Code
                  </label>
                  <p className="text-sm text-gray-600 mb-3">
                    We sent a code to <strong>{email}</strong>
                  </p>
                  <div className="relative">
                    <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      id="otp"
                      type="text"
                      value={otp}
                      onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      disabled={loading}
                      required
                      maxLength={6}
                      className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-center text-2xl tracking-widest font-mono"
                      placeholder="000000"
                      autoComplete="one-time-code"
                    />
                  </div>
                </div>

                {/* Trading Mode Selection */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-3">
                    Trading Mode <span className="text-red-600">*</span>
                  </label>
                  <div className="space-y-3">
                    <label
                      className={`flex items-start gap-3 p-4 border-2 rounded-lg cursor-pointer transition-all ${
                        tradingMode === 'PAPER'
                          ? 'border-emerald-500 bg-emerald-50'
                          : 'border-gray-300 hover:border-gray-400'
                      }`}
                    >
                      <input
                        type="radio"
                        name="trading_mode"
                        value="PAPER"
                        checked={tradingMode === 'PAPER'}
                        onChange={(e) => setTradingMode(e.target.value as TradingMode)}
                        className="mt-1"
                      />
                      <div className="flex-1">
                        <div className="font-semibold text-gray-900">PAPER (Mock Account)</div>
                        <div className="text-sm text-gray-600 mt-1">
                          Simulated trading with no real money. Safe for testing.
                        </div>
                      </div>
                    </label>

                    <label
                      className={`flex items-start gap-3 p-4 border-2 rounded-lg cursor-pointer transition-all ${
                        tradingMode === 'LIVE'
                          ? 'border-red-500 bg-red-50'
                          : 'border-gray-300 hover:border-gray-400'
                      }`}
                    >
                      <input
                        type="radio"
                        name="trading_mode"
                        value="LIVE"
                        checked={tradingMode === 'LIVE'}
                        onChange={(e) => setTradingMode(e.target.value as TradingMode)}
                        className="mt-1"
                      />
                      <div className="flex-1">
                        <div className="font-semibold text-gray-900">LIVE (Real Broker)</div>
                        <div className="text-sm text-gray-600 mt-1">
                          Real trades with real money. <strong className="text-red-600">Use with caution.</strong>
                        </div>
                      </div>
                    </label>
                  </div>

                  {tradingMode === 'LIVE' && (
                    <div className="mt-3 flex items-start gap-2 p-3 rounded-lg bg-yellow-50 border border-yellow-200">
                      <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-yellow-800">
                        <strong>Warning:</strong> LIVE trading places real orders with real money. You will be asked to confirm before proceeding.
                      </p>
                    </div>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={loading || otp.length !== 6}
                  className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-emerald-700 text-white font-semibold rounded-lg hover:bg-emerald-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-md"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Verifying...
                    </>
                  ) : (
                    <>
                      <Lock className="w-5 h-5" />
                      Continue
                    </>
                  )}
                </button>

                <button
                  type="button"
                  onClick={handleBackToEmail}
                  disabled={loading}
                  className="w-full text-sm text-gray-600 hover:text-emerald-700 font-medium transition-colors"
                >
                  ← Back to email
                </button>
              </form>
            ) : (
              /* LIVE Confirmation Step */
              <form onSubmit={handleLiveConfirm} className="space-y-6">
                <div className="p-4 rounded-lg bg-red-50 border-2 border-red-200">
                  <div className="flex items-start gap-3 mb-4">
                    <AlertTriangle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <h3 className="font-bold text-red-900 text-lg">LIVE Trading Confirmation</h3>
                      <p className="text-sm text-red-800 mt-1">
                        You are about to enable LIVE trading mode. This will place real orders with real money.
                      </p>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <label className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        checked={liveConfirmChecked}
                        onChange={(e) => setLiveConfirmChecked(e.target.checked)}
                        className="mt-1"
                      />
                      <span className="text-sm text-red-900">
                        I understand that LIVE trading will place real orders with real money and I accept full responsibility for all trades.
                      </span>
                    </label>

                    <div>
                      <label htmlFor="confirm-text" className="block text-sm font-semibold text-red-900 mb-2">
                        Type "LIVE" to confirm:
                      </label>
                      <input
                        id="confirm-text"
                        type="text"
                        value={liveConfirmText}
                        onChange={(e) => setLiveConfirmText(e.target.value)}
                        className="w-full px-4 py-2 border-2 border-red-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                        placeholder="Type LIVE"
                      />
                    </div>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading || !liveConfirmChecked || liveConfirmText.toUpperCase() !== 'LIVE'}
                  className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-red-600 text-white font-semibold rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-md"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Confirming...
                    </>
                  ) : (
                    <>
                      <AlertTriangle className="w-5 h-5" />
                      Confirm LIVE Trading
                    </>
                  )}
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setStep('otp');
                    setTradingMode('PAPER');
                    setLiveConfirmChecked(false);
                    setLiveConfirmText('');
                    setError('');
                  }}
                  disabled={loading}
                  className="w-full text-sm text-gray-600 hover:text-emerald-700 font-medium transition-colors"
                >
                  ← Back to trading mode selection
                </button>
              </form>
            )}
          </div>

          <div className="px-8 py-6 bg-gray-50 border-t border-gray-200">
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Lock className="w-4 h-4" />
              <span>Access is by invitation only</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
